/** Paths we must never send the user back to after logging in, to avoid a redirect loop. */
const AUTH_PATHS = ['/login', '/signup', '/forgot-password', '/verification'];

/** Control characters and whitespace, which can smuggle a different target past the checks below. */
// eslint-disable-next-line no-control-regex -- matching control characters is the point here
const UNSAFE_CHARACTERS = /[\u0000-\u0020]/;

/**
 * Validates a post-login redirect target.
 *
 * The value ends up in `window.location.href`, so only same-origin, app-relative paths are
 * accepted: anything that could point at another origin (absolute urls, protocol-relative
 * `//host`, `javascript:` urls) is rejected.
 *
 * @returns the sanitized path, or null when the value cannot be trusted.
 */
export const sanitizeReturnUrl = (
  rawReturnUrl: string | null | undefined
): string | null => {
  if (!rawReturnUrl) {
    return null;
  }

  let returnUrl: string;
  try {
    returnUrl = decodeURIComponent(rawReturnUrl);
  } catch {
    return null;
  }

  if (!returnUrl.startsWith('/')) {
    return null;
  }

  // `//host` and `/\host` are read as protocol-relative urls by the browser
  if (returnUrl.startsWith('//') || returnUrl.startsWith('/\\')) {
    return null;
  }

  if (UNSAFE_CHARACTERS.test(returnUrl)) {
    return null;
  }

  const isAuthPath = AUTH_PATHS.some(
    (authPath) =>
      returnUrl === authPath ||
      returnUrl.startsWith(`${authPath}/`) ||
      returnUrl.startsWith(`${authPath}?`)
  );
  if (isAuthPath) {
    return null;
  }

  return returnUrl;
};
